import { useState } from 'react';
import { AppRoute } from '@/App';
import { useNavigate } from 'react-router-dom';
import { useTimer } from '@/hooks/useTimer';
import { authService } from '@/services/authService';
import { AxiosError } from 'axios';

enum ForgotPasswordStep {
  Step1 = 1,
  Step2 = 2,
}

export interface ForgotPasswordScreenViewModel {
  // Состояние формы
  email: string;
  emailError: string | null;
  step: number;
  resetCode: string;
  firstPassword: string;
  secondPassword: string;
  error: string | null;
  // Методы управления формой
  setEmail: (email: string) => void;
  handleResetCodeChange: (code: string) => void;
  handleFirstPasswordChange: (password: string) => void;
  handleSecondPasswordChange: (password: string) => void;
  // Методы валидации
  sandNewCode: () => void;
  // Методы восстановления пароля
  handleSubmitStep1: () => void; 
  handleSubmitStep2: () => void; 
  firstPasswordError: string | null;
  secondPasswordError: string | null;
  firstPasswordVisible: boolean;
  secondPasswordVisible: boolean;
  navigateBack: () => void;
  setFirstPasswordVisible: (visible: boolean) => void;
  setSecondPasswordVisible: (visible: boolean) => void;
  time: number;
}

export const useForgotPasswordScreenViewModel = (): ForgotPasswordScreenViewModel => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [step, setStep] = useState(ForgotPasswordStep.Step1);
  const [resetCode, setResetCode] = useState("");
  const [firstPassword, setFirstPassword] = useState("");
  const [firstPasswordError, setFirstPasswordError] = useState<string | null>(null);
  const [secondPassword, setSecondPassword] = useState("");
  const [secondPasswordError, setSecondPasswordError] = useState<string | null>(null);
  const [firstPasswordVisible, setFirstPasswordVisible] = useState(false);
  const [secondPasswordVisible, setSecondPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    time,
    start,
  } = useTimer({
    persistKey: "main-timer",
    onComplete: () => {
      console.log('Таймер завершен!');
    }
  });

  const navigate = useNavigate();
  const navigateBack = () => {
    if (step === ForgotPasswordStep.Step1) {
      navigate(AppRoute.Login);
    } else {
      setStep(ForgotPasswordStep.Step1);
    }
  };

  const validateEmail = (email: string):boolean => {
    if (!email.trim()) {
      setEmailError("Введите email");
      return false;
    }
    if (!email.includes("@")) {
      setEmailError("Введите правильный email");
      return false;
    }
    setEmailError(null);
    return true;
  };

  const validateFirstPassword = (password: string): boolean => {
    if (!password.trim()) {
      setFirstPasswordError("Введите пароль");
      return false;
    }
    return true;
  };

  const validateSecondPassword = (password: string): boolean => {
    if (!password.trim()) {
      setSecondPasswordError("Введите пароль");
      return false;
    }
    return true;
  };

  const validateForm = (): boolean => {
    const firstPasswordValidation = validateFirstPassword(firstPassword);
    const secondPasswordValidation = validateSecondPassword(secondPassword);
    return firstPasswordValidation && secondPasswordValidation && validatePasswordsEqual();
  };

  const validatePasswordsEqual = (): boolean => {
    if (firstPassword.trim()===secondPassword.trim()) {
      return true;
    }
    setError("Пароли не совпадают");
    return false;
  };

  const handleSubmitStep1 = async () => {
    if (validateEmail(email)) {
      try {
        setStep(ForgotPasswordStep.Step2);
      } catch (error) {
        setEmailError("Некорректный email");
      }
    }
  };

  const handleSubmitStep2 = async () => {
    if (validateForm()) {
      try { 
        await authService.resetPassword(email, resetCode, firstPassword);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 400) {
          setError('Некорректный код');
        } else {
          setError('Ошибка при сбросе пароля:');
        }
      }
    }
  };
  
  const handleResetCodeChange = (code: string) => {
    setResetCode(code);
  };
  const handleFirstPasswordChange = (password: string) => {
    setFirstPassword(password);
  };
  const handleSecondPasswordChange = (password: string) => {  
    setSecondPassword(password);
  };

  const sandNewCode = async () => {
    if (time!==0) return;
    try {
     await authService.sendResetCode(email);
      start(60);
    } catch (error) {
      console.error('Ошибка при отправке email');
    }
    
  };
  
  return {
    setFirstPasswordVisible,
    setSecondPasswordVisible,
    time,
    email,
    emailError,
    step,
    setEmail,
    handleSubmitStep1,
    handleSubmitStep2,
    navigateBack,
    resetCode,
    firstPassword,
    secondPassword,
    firstPasswordError,
    secondPasswordError,
    firstPasswordVisible,
    secondPasswordVisible,
    error,
    handleResetCodeChange,
    handleFirstPasswordChange,
    handleSecondPasswordChange,
    sandNewCode,
  };
}; 
